import { useState } from "react";
import { Check, Palette, X } from "lucide-react";
import { useColorTheme } from "@/context/ColorThemeContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";

function ThemeSwatches({ colors, compact = false }) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 overflow-hidden rounded-full ring-2 ring-background shadow-sm ${compact ? "h-7 w-[54px]" : "h-8 w-12"}`}
    >
      {colors.map((color) => (
        <span key={color} className="flex-1" style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

function ThemeChoices({ theme, themes, onSelect, compact = false }) {
  return (
    <div className={compact ? "grid grid-cols-3 gap-2" : "space-y-2"}>
      {themes.map((option) => {
        const selected = option.id === theme;

        return (
          <button
            key={option.id}
            type="button"
            data-testid={`color-theme-${option.id}${compact ? "-mobile" : ""}`}
            aria-pressed={selected}
            onClick={() => onSelect(option.id)}
            className={`relative border text-start transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 ${
              compact
                ? "flex min-h-[108px] flex-col items-center justify-center gap-2 rounded-[20px] px-2 py-3 text-center"
                : "flex w-full items-center gap-3 rounded-[18px] p-3"
            } ${
              selected
                ? "border-primary/35 bg-primary/[0.065]"
                : "border-border/70 bg-muted/45 hover:border-primary/25 hover:bg-muted"
            }`}
          >
            <ThemeSwatches colors={option.swatches} compact={compact} />

            <span className={compact ? "min-w-0" : "min-w-0 flex-1"}>
              <span className={`${compact ? "text-[11px] leading-4" : "text-xs"} block font-extrabold text-foreground`}>
                {compact ? option.shortLabel : option.label}
              </span>
              {!compact && (
                <span className="mt-0.5 block text-[9px] leading-4 text-muted-foreground">
                  {option.description}
                </span>
              )}
            </span>

            <span
              aria-hidden="true"
              className={`${compact ? "absolute left-2 top-2 h-5 w-5" : "h-6 w-6 shrink-0"} grid place-items-center rounded-full ${
                selected ? "bg-primary text-primary-foreground" : "bg-card text-transparent"
              }`}
            >
              <Check className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function ColorThemeSwitcher({ variant = "responsive", className = "" }) {
  const { theme, setTheme, themes } = useColorTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeTheme = themes.find((item) => item.id === theme);
  const ariaLabel = `تخصيص مظهر التطبيق، المحدد حاليًا: ${activeTheme?.label}`;
  const showMobile = variant !== "desktop";
  const showDesktop = variant !== "mobile";

  const selectMobileTheme = (nextTheme) => {
    setTheme(nextTheme);
    setMobileOpen(false);
  };

  return (
    <>
      {showMobile && (
        <Drawer
          open={mobileOpen}
          onOpenChange={setMobileOpen}
          shouldScaleBackground={false}
        >
          <DrawerTrigger asChild>
            <button
              type="button"
              data-testid="color-theme-trigger"
              aria-label={ariaLabel}
              title="تخصيص المظهر"
              className={`grid h-11 w-11 shrink-0 place-items-center rounded-full border border-border bg-card text-primary shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 ${variant === "responsive" ? "md:hidden" : ""} ${className}`}
            >
              <Palette className="h-[17px] w-[17px]" />
            </button>
          </DrawerTrigger>

          <DrawerContent
            overlayClassName="bg-brand/55 backdrop-blur-[2px]"
            className="rounded-t-[30px] border-border bg-card px-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-brand"
          >
            <DrawerHeader className="relative px-1 pb-4 pt-2 text-start">
              <DrawerTitle className="text-base font-extrabold">تخصيص المظهر</DrawerTitle>
              <DrawerDescription className="mt-1 text-xs leading-5">
                اختر الهوية الأقرب لك. سيُحفظ اختيارك تلقائيًا.
              </DrawerDescription>
              <DrawerClose asChild>
                <button
                  type="button"
                  aria-label="إغلاق"
                  className="absolute left-0 top-1 grid h-9 w-9 place-items-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-border hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </DrawerClose>
            </DrawerHeader>

            <ThemeChoices
              theme={theme}
              themes={themes}
              onSelect={selectMobileTheme}
              compact
            />
          </DrawerContent>
        </Drawer>
      )}

      {showDesktop && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="color-theme-trigger-desktop"
              aria-label={ariaLabel}
              title="تخصيص المظهر"
              className={`h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-card text-primary shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 ${variant === "responsive" ? "hidden md:inline-flex" : "inline-flex"} ${className}`}
            >
              <Palette className="h-4 w-4" />
            </button>
          </PopoverTrigger>

          <PopoverContent
            side="bottom"
            align="end"
            sideOffset={10}
            className="w-[320px] rounded-[24px] border-border bg-card p-3 shadow-card"
          >
            <div className="px-2 pb-3 pt-1">
              <h2 className="text-sm font-extrabold">تخصيص المظهر</h2>
              <p className="mt-1 text-[10px] leading-5 text-muted-foreground">
                اختر هوية الألوان المناسبة لك، وسنحفظ اختيارك تلقائيًا.
              </p>
            </div>

            <ThemeChoices theme={theme} themes={themes} onSelect={setTheme} />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
